/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import React, {CSSProperties, MouseEventHandler, useMemo} from 'react';

import {getImg} from '../../util/utils';

import {ImageSources} from '../../util/interfaces';

interface Props {
    name: string;
    alt?: string;
    fallbackExtension?: string;
    className?: string;
    onLoad?: VoidFunction;
    onClick?: MouseEventHandler;
    onDoubleClick?: MouseEventHandler;
    style?: CSSProperties;
}

export default function Image(props: Props) {
    const {className, name, onLoad, onClick, onDoubleClick} = props;
    const fallbackExtension = props.fallbackExtension ?? 'png';
    const alt = props.alt ?? 'Bolmate';
    const [sources, fallbackSource]: [ImageSources, string] = useMemo(() => {
        const sources = getImg(name, fallbackExtension);
        const fallback = sources.length ? sources[sources.length - 1][0] : '';
        return [sources, fallback];
    }, [name, fallbackExtension]);

    function renderSource(source: [string, string], index: number) {
        return <source key={index} srcSet={source[0]} type={source[1]} />;
    }

    const pictureStyle = {
        maxWidth: 'unset',
        width: 'unset',
        maxHeight: 'unset',
        height: 'unset',
        display: 'flex',
        justifyContent: 'center',
        ...props.style
    };
    return (
        <picture
            onLoad={onLoad}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            style={pictureStyle}
            className={className}
        >
            {sources.map(renderSource)}
            <img
                src={fallbackSource}
                alt={alt}
                className={className}
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                onLoad={onLoad}
                style={props.style}
            />
        </picture>
    );
}
